import { Event } from '@/types/revenue-manager';

/**
 * Raw API response format for events (can have either snake_case or camelCase)
 */
export interface RawApiEvent {
  id?: string | number;
  org_id?: string | number;
  orgId?: string | number;
  property_id?: number;
  propertyId?: number;
  name?: string;
  location?: string;
  startDate?: string;
  start_date?: string;
  endDate?: string;
  end_date?: string;
  category?: 'festival' | 'conference' | 'sports_event' | 'concert' | 'holiday' | 'convention' | 'exhibition' | 'seasonal' | 'other';
  impact?: 'low' | 'medium' | 'high';
  description?: string | null;
  pricingMultiplier?: number | string;
  pricing_multiplier?: number | string;
  propertyIds?: string[];
  property_ids?: string[];
  affected_properties?: string | string[] | null;
  targets?: string | null;
  submitted_by?: string | number | null;
  created_by?: number;
  updated_by?: number | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * Transform event from API format to internal format
 */
export function transformEventFromAPI(rawEvent: RawApiEvent): Event {
  // Parse affected_properties if it's a JSON string
  let propertyIds: string[] = [];
  if (rawEvent.affected_properties) {
    if (typeof rawEvent.affected_properties === 'string') {
      try {
        propertyIds = JSON.parse(rawEvent.affected_properties);
      } catch {
        propertyIds = [rawEvent.affected_properties];
      }
    } else if (Array.isArray(rawEvent.affected_properties)) {
      propertyIds = rawEvent.affected_properties;
    }
  } else if (rawEvent.propertyIds) {
    propertyIds = rawEvent.propertyIds;
  } else if (rawEvent.property_id) {
    propertyIds = [String(rawEvent.property_id)];
  }
  
  // Parse pricing_multiplier (can be string or number)
  let pricingMultiplier = 1.0;
  if (rawEvent.pricingMultiplier !== undefined) {
    pricingMultiplier = typeof rawEvent.pricingMultiplier === 'string' 
      ? parseFloat(rawEvent.pricingMultiplier) 
      : rawEvent.pricingMultiplier;
  } else if (rawEvent.pricing_multiplier !== undefined) {
    pricingMultiplier = typeof rawEvent.pricing_multiplier === 'string'
      ? parseFloat(rawEvent.pricing_multiplier)
      : rawEvent.pricing_multiplier;
  }
  
  return {
    id: String(rawEvent.id || ''),
    org_id: String(rawEvent.org_id || rawEvent.orgId || ''),
    name: rawEvent.name || 'Unnamed Event',
    location: rawEvent.location || undefined,
    startDate: rawEvent.startDate || rawEvent.start_date || new Date().toISOString().split('T')[0],
    endDate: rawEvent.endDate || rawEvent.end_date || new Date().toISOString().split('T')[0],
    category: rawEvent.category || 'other',
    impact: rawEvent.impact || 'medium',
    description: rawEvent.description || undefined,
    pricingMultiplier: pricingMultiplier,
    propertyIds: propertyIds.map(id => String(id)),
  };
}

/**
 * Transform event from internal format to API format
 */
export function transformEventForAPI(event: Omit<Event, 'id'>): Record<string, unknown> {
  return {
    org_id: event.org_id,
    name: event.name,
    location: event.location || null,
    startDate: event.startDate,
    endDate: event.endDate,
    category: event.category,
    impact: event.impact,
    description: event.description || null,
    pricingMultiplier: event.pricingMultiplier,
    propertyIds: event.propertyIds,
  };
}
